import { useState } from 'react';
import type { EventType } from '../../../types/event';

const DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const RECURRENCE_LABELS: Record<string, string> = {
  NONE: 'Sem recorrência',
  WEEKLY: 'Semanal',
  BIWEEKLY: 'Quinzenal',
  MONTHLY: 'Mensal',
};

interface Props {
  eventTypes: EventType[];
  onAdd: (payload: { name: string; recurrence_pattern: string; default_day_of_week?: number | null }) => Promise<void>;
  onDelete: (typeId: string) => Promise<void>;
  onGenerateOpen: (typeId: string) => void;
}

export default function EventTypeManager({ eventTypes, onAdd, onDelete, onGenerateOpen }: Props) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [recurrence, setRecurrence] = useState('NONE');
  const [dayOfWeek, setDayOfWeek] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      await onAdd({
        name: name.trim(),
        recurrence_pattern: recurrence,
        default_day_of_week: dayOfWeek !== '' ? Number(dayOfWeek) : null,
      });
      setName('');
      setRecurrence('NONE');
      setDayOfWeek('');
      setShowForm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white/80">Tipos de Evento</h3>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="text-xs text-[#C8A96E] hover:text-[#D4B87A] transition-colors"
        >
          + Novo tipo
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-white/[0.03] border border-white/10 rounded-xl p-4 space-y-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do tipo"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#C8A96E]/50"
            required
          />
          <select
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none"
          >
            {Object.entries(RECURRENCE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          {recurrence !== 'NONE' && (
            <select
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none"
            >
              <option value="">Dia padrão (opcional)</option>
              {DAYS.map((d, i) => (
                <option key={i} value={i}>
                  {d}
                </option>
              ))}
            </select>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="flex-1 py-2 border border-white/10 text-white/50 rounded-lg text-xs transition-colors hover:text-white"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-lg text-xs disabled:opacity-50"
            >
              Criar
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {eventTypes.length === 0 && (
          <p className="text-xs text-white/30 text-center py-4">Nenhum tipo cadastrado</p>
        )}
        {eventTypes.map((et) => (
          <div
            key={et.id}
            className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3"
          >
            <div>
              <p className="text-sm text-white">{et.name}</p>
              <p className="text-xs text-white/30 mt-0.5">
                {RECURRENCE_LABELS[et.recurrence_pattern]}
                {et.default_day_of_week != null && ` · ${DAYS[et.default_day_of_week]}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {et.recurrence_pattern !== 'NONE' && (
                <button
                  onClick={() => onGenerateOpen(et.id)}
                  className="text-xs text-[#C8A96E] hover:text-[#D4B87A] transition-colors"
                >
                  Gerar
                </button>
              )}
              <button
                onClick={() => onDelete(et.id)}
                className="text-xs text-white/30 hover:text-red-400 transition-colors"
              >
                Remover
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
